/**
 * `04/ DL Files` — the cross-CRF file register at `/countries/:cc/dl-files`.
 *
 * ===========================================================================
 * "UNKNOWN" IS A FACT, NOT A GAP
 * ===========================================================================
 * `attachments.created_by` is `ON DELETE SET NULL`, so a DL file can outlive the
 * account that uploaded it and the API reports `uploaded_by: null` rather than
 * borrowing the work order's current assignee (the service says so at the field
 * itself). This screen holds that line: a null uploader renders as a muted
 * "Unknown" pill carrying the reason in its `title`. Substituting ANY other name
 * on the row — the tester, the assignee, the customer — would be this client
 * inventing provenance for a build artefact, which is the one column on this
 * screen somebody would quote in an argument.
 *
 * ===========================================================================
 * THE DOWNLOAD IS A BUTTON, AND THAT IS NOT A STYLE CHOICE
 * ===========================================================================
 * The bytes live at `GET /work-orders/:id/files/:fileId`, behind the JWT guard.
 * An `<a href>` carries no `Authorization` header, so it would 401 and render as
 * a link that does nothing at all. The bytes therefore come through
 * `downloadRegisterFile` → `fetchBinary` (which also means the request obeys the
 * single-flight refresh discipline, D-web-1), and the blob is handed to the
 * browser as an object URL that is revoked immediately after the click so a
 * long-lived register does not accumulate blobs of customer files.
 *
 * RULE 11: a download that fails silently is the worst version of this — the
 * operator concludes the file is gone. A failure raises a page-level Alert.
 *
 * ===========================================================================
 * NO FILTER CONTROLS, DELIBERATELY
 * ===========================================================================
 * The route accepts `?crf=` and `?uploader=`, and this screen sets neither: the
 * design draws no control for either, and a filter that only a hand-typed URL
 * can set is a filter nobody can clear. The register is therefore honestly
 * unfiltered — every DL file in the country, newest first.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { relativeTime } from '../../domain/events';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
// Reused, not re-authored: `formatBytes` encodes the 1 MB = 1024×1024
// arithmetic that the upload cap and the API's own error message agree on, and
// `fileKind` encodes ESC-14 — the extension is a fact, a MIME type for a TDL is
// not. A second copy of either would be a second place for them to drift.
import { fileKind, formatBytes } from '../work-pipeline/WorkFilesPanel';
import { downloadRegisterFile, fetchDlFilesRegister, registerErrorText, registerKeys } from './api';
import type { DlFileRegisterRow, DlFilesRegisterFilters } from './api';

const PER_PAGE = 50;

export function DlFilesRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters: DlFilesRegisterFilters = { page, perPage: PER_PAGE };

  const list = useQuery({
    queryKey: registerKeys.dlFiles(countryCode, filters),
    queryFn: ({ signal }) => fetchDlFilesRegister(countryCode, filters, signal),
    enabled: canView,
  });

  /** A failed DOWNLOAD. Page-level, because the failure is about the transfer,
   * not about the row — and a message buried in a cell would be missed. */
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (!canView) {
    // D5 — the refusal is a designed state naming who to ask. Never an empty
    // table, which would read as "nothing has been uploaded".
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="DL files are not available to this account">
          Viewing the DL file register in {countryCode} needs the CRF permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="DL Files"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(list.data.total)} ${list.data.total === 1 ? 'file' : 'files'}`
        }
      />

      {downloadError === null ? null : (
        <Alert tone="danger" title="That file could not be downloaded">
          {downloadError}
        </Alert>
      )}

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the DL file register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The DL file register could not be loaded">
          <p>{registerErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes list's V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} {total === 1 ? 'file' : 'files'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState title="No DL files have been uploaded in this country yet." />
        </div>
      ) : (
        <DlFileTable
          countryCode={countryCode}
          rows={rows}
          onFailure={(text) => setDownloadError(text)}
          onAttempt={() => setDownloadError(null)}
        />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

const HEADERS = ['FILE', 'CRF', 'CUSTOMER', 'UPLOADED BY', 'SIZE'] as const;

function DlFileTable({
  countryCode,
  rows,
  onFailure,
  onAttempt,
}: {
  countryCode: string;
  rows: readonly DlFileRegisterRow[];
  onFailure: (text: string) => void;
  onAttempt: () => void;
}): React.JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null);

  const download = async (row: DlFileRegisterRow): Promise<void> => {
    onAttempt();
    setBusyId(row.id);
    try {
      const result = await downloadRegisterFile(row.work_order_id, row.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      // The server's name wins; the row's is the fallback. Neither is a path.
      anchor.download = result.filename ?? row.file_name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      onFailure(registerErrorText(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1080px] border-collapse text-left">
        <caption className="sr-only">Every DL file uploaded in {countryCode}</caption>
        <colgroup>
          <col />
          <col className="w-[170px]" />
          <col className="w-[170px]" />
          <col className="w-[160px]" />
          <col className="w-[100px]" />
          <col className="w-[150px]" />
          <col className="w-[130px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {HEADERS.map((header) => (
              <th
                key={header}
                scope="col"
                className={cn(
                  'px-3 text-th uppercase text-fg-muted',
                  header === 'SIZE' && 'text-right',
                )}
              >
                {header}
              </th>
            ))}
            {/* Server-ordered, newest first — stated, not a dead sort control. */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              DATE <span aria-hidden="true">↓</span>
              <span className="sr-only">, newest first</span>
            </th>
            <th scope="col" className="px-3 text-right text-th uppercase text-fg-muted">
              DOWNLOAD
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
              <td className="max-w-0 px-3 py-3 align-middle">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span title={row.file_name} className="truncate text-body13 text-fg">
                    {row.file_name}
                  </span>
                  {/* ESC-14: the extension is a fact; a MIME type for a TDL is not. */}
                  <span className="text-label text-fg-muted">{fileKind(row.file_name)}</span>
                </div>
              </td>

              <td className="px-3 py-3 align-middle">
                <Link
                  to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                  className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                >
                  {row.crf_doc_no}
                </Link>
              </td>

              <td className="px-3 py-3 align-middle text-body13 text-fg">{row.party_name}</td>

              <td className="px-3 py-3 align-middle">
                {row.uploaded_by === null ? (
                  <span
                    title="The account that uploaded this file no longer exists, so it cannot be named."
                    className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-1 text-label text-fg-muted ring-1 ring-line"
                  >
                    Unknown
                  </span>
                ) : (
                  <span className="text-body13 text-fg">{row.uploaded_by.name}</span>
                )}
              </td>

              <td className="tnum px-3 py-3 text-right align-middle text-body13 text-fg-secondary">
                {formatBytes(row.size_bytes)}
              </td>

              <td className="px-3 py-3 align-middle">
                <div className="flex flex-col gap-0.5">
                  <span className="text-body13 text-fg">{formatDateLong(row.created_at)}</span>
                  <span className="text-label text-fg-muted">{relativeTime(row.created_at)}</span>
                </div>
              </td>

              <td className="px-3 py-3 text-right align-middle">
                <button
                  type="button"
                  disabled={busyId === row.id}
                  aria-label={`Download ${row.file_name}`}
                  onClick={() => {
                    void download(row);
                  }}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-body13 font-medium text-indigo hover:underline disabled:opacity-60"
                >
                  <Download aria-hidden="true" className="size-3.5 shrink-0" />
                  {busyId === row.id ? 'Downloading…' : 'Download'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/**
 * `Table/Pagination` — re-authored per screen, the house pattern `CrfsListPage`
 * and `WorkConfirmationPage` both follow: the geometry is shared, the component
 * is file-private in each.
 */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
